import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';
import { cvDownloadAdres, useCvMutaties } from '../../api/cvs.ts';
import type { CvDetails, KandidaatProfiel } from '../../api/typen.ts';
import { BevestigDialoog } from '../../componenten/BevestigDialoog.tsx';
import { LegeStaat } from '../../componenten/Laadstatus.tsx';
import { useMeldingen } from '../../componenten/Meldingen.tsx';
import { StatusChip } from '../../componenten/StatusChip.tsx';
import { bestandsgrootte, datumTijd } from '../../helpers/formatteer.ts';
import { IndelingFormulier } from './IndelingFormulier.tsx';
import { ProfielVerrijkenDialoog } from './ProfielVerrijkenDialoog.tsx';

const toegestaan = '.pdf,.docx,.odt,.doc,application/pdf';

/**
 * De cv's van een kandidaat: uploaden, downloaden, actueel maken, indelen en verwijderen.
 * @param props Het profiel van de kandidaat.
 * @returns Het paneel.
 */
export function CvsPaneel({ kandidaat }: { kandidaat: KandidaatProfiel }) {
  const mutaties = useCvMutaties();
  const { meld, meldFout } = useMeldingen();
  const bestandsinvoer = useRef<HTMLInputElement>(null);
  const [actueel, setActueel] = useState(true);
  const [indeling, setIndeling] = useState<CvDetails | null>(null);
  const [verwijderen, setVerwijderen] = useState<CvDetails | null>(null);
  const [ontkoppel, setOntkoppel] = useState(false);
  const [verrijkNaUpload, setVerrijkNaUpload] = useState(true);
  const [verrijken, setVerrijken] = useState<{ id: string; bestandsnaam: string } | null>(null);

  const upload = (bestand: File | undefined) => {
    if (!bestand) {
      return;
    }
    mutaties.upload
      .mutateAsync({ kandidaatId: kandidaat.id, bestand, actueel })
      .then((cv) => {
        meld('Cv geüpload');
        if (verrijkNaUpload) {
          setVerrijken({ id: cv.id, bestandsnaam: cv.bestandsnaam });
        }
      })
      .catch(meldFout)
      .finally(() => {
        if (bestandsinvoer.current) {
          bestandsinvoer.current.value = '';
        }
      });
  };

  return (
    <>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: { sm: 'center' }, mb: 2 }}>
        <input
          ref={bestandsinvoer}
          type="file"
          accept={toegestaan}
          hidden
          onChange={(gebeurtenis) => upload(gebeurtenis.target.files?.[0])}
        />
        <Button
          variant="contained"
          startIcon={<UploadFileIcon />}
          loading={mutaties.upload.isPending}
          onClick={() => bestandsinvoer.current?.click()}
        >
          Cv uploaden
        </Button>
        <FormControlLabel
          control={<Checkbox checked={actueel} onChange={(gebeurtenis) => setActueel(gebeurtenis.target.checked)} />}
          label="Nieuw cv wordt het actuele cv"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={verrijkNaUpload}
              onChange={(gebeurtenis) => setVerrijkNaUpload(gebeurtenis.target.checked)}
            />
          }
          label="Daarna profiel verrijken met AI"
        />
        <Typography variant="body2" color="text.secondary">
          Pdf, docx, odt of doc tot 10 MB.
        </Typography>
      </Stack>
      {kandidaat.cvs.length === 0 ? (
        <LegeStaat>Nog geen cv geüpload.</LegeStaat>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Bestand</TableCell>
                <TableCell>Geüpload</TableCell>
                <TableCell>Verwerking</TableCell>
                <TableCell>Indeling</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {kandidaat.cvs.map((cv) => (
                <TableRow key={cv.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      {cv.isActueel && (
                        <Tooltip title="Actueel cv">
                          <StarIcon fontSize="small" color="warning" />
                        </Tooltip>
                      )}
                      <Link href={cvDownloadAdres(cv.id)} underline="hover" sx={{ fontWeight: 500 }}>
                        {cv.bestandsnaam}
                      </Link>
                      <Typography variant="caption" color="text.secondary">
                        {bestandsgrootte(cv.grootteBytes)}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>{datumTijd(cv.geuploadOp)}</TableCell>
                  <TableCell>
                    <StatusChip soort="verwerking" waarde={cv.verwerkingsstatus} />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                      {cv.functietitels.map((koppeling) => (
                        <Chip key={koppeling.functietitelId} label={koppeling.functietitel.naam} size="small" />
                      ))}
                      {cv.talentpools.map((koppeling) => (
                        <Chip
                          key={koppeling.talentpoolId}
                          label={koppeling.talentpool.naam}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Downloaden">
                      <IconButton size="small" href={cvDownloadAdres(cv.id)} aria-label="Downloaden">
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {!cv.isActueel && (
                      <Tooltip title="Maak dit het actuele cv">
                        <IconButton
                          size="small"
                          aria-label="Actueel maken"
                          onClick={() =>
                            mutaties.maakActueel
                              .mutateAsync({ id: cv.id, kandidaatId: kandidaat.id })
                              .then(() => meld('Cv is nu actueel'))
                              .catch(meldFout)
                          }
                        >
                          <StarBorderIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Profiel verrijken met AI">
                      <IconButton
                        size="small"
                        aria-label="Profiel verrijken"
                        onClick={() => setVerrijken({ id: cv.id, bestandsnaam: cv.bestandsnaam })}
                      >
                        <AutoAwesomeIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Indeling">
                      <IconButton size="small" aria-label="Indeling" onClick={() => setIndeling(cv)}>
                        <LabelOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Verwijderen">
                      <IconButton size="small" aria-label="Verwijderen" onClick={() => setVerwijderen(cv)}>
                        <DeleteOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {indeling && (
        <IndelingFormulier
          open
          onSluit={() => setIndeling(null)}
          titel={`Indeling van ${indeling.bestandsnaam}`}
          functietitels={indeling.functietitels.map((koppeling) => koppeling.functietitel)}
          talentpools={indeling.talentpools.map((koppeling) => koppeling.talentpool)}
          opslaan={(invoer) => mutaties.zetIndeling.mutateAsync({ id: indeling.id, kandidaatId: kandidaat.id, invoer })}
        />
      )}
      <ProfielVerrijkenDialoog
        open={verrijken !== null}
        onSluit={() => setVerrijken(null)}
        kandidaat={kandidaat}
        cv={verrijken}
      />
      <BevestigDialoog
        open={verwijderen !== null}
        titel="Cv verwijderen"
        bevestigLabel="Verwijderen"
        gevaarlijk
        bezig={mutaties.verwijder.isPending}
        onSluit={() => setVerwijderen(null)}
        onBevestig={() => {
          if (verwijderen) {
            mutaties.verwijder
              .mutateAsync({ id: verwijderen.id, kandidaatId: kandidaat.id, ontkoppelKwalificaties: ontkoppel })
              .then(() => {
                meld('Cv verwijderd');
                setVerwijderen(null);
              })
              .catch(meldFout);
          }
        }}
      >
        <Typography sx={{ mb: 1 }}>Het bestand {verwijderen?.bestandsnaam} wordt definitief verwijderd.</Typography>
        <FormControlLabel
          control={
            <Checkbox checked={ontkoppel} onChange={(gebeurtenis) => setOntkoppel(gebeurtenis.target.checked)} />
          }
          label="Kwalificaties uit dit cv bij de kandidaat laten staan"
        />
      </BevestigDialoog>
    </>
  );
}
